
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Menu from "../../../assets/icons/Menu";
import OrangeCross from "../../../assets/icons/OrangeCross";
import User from "../../../assets/icons/User";
import Logo from "../../../assets/img/logo.png";
import { LoginHelper } from "../../../utils/LoginHelper";
import PwaInstall from "./PwaInstall";

const NAV_ITEMS = [
	{ to: "/video_clips/", label: "کلیپ", match: "/video_clip" },
	{ to: "/webinar_list/", label: "وبینارها", match: "/webinar" },
	{ to: "/publication_list/", label: "مقالات", match: "/publication" },
	{ to: "/subscriptions/", label: "اشتراک‌ها", match: "/subscriptions" },
];

function isNavActive(pathname, match) {
	return pathname.includes(match);
}

function NavItem({ to, label, match, onLinkClick }) {
	const { pathname } = useLocation();
	const active = isNavActive(pathname, match);

	return (
		<Link
			to={to}
			onClick={onLinkClick}
			className={[
				"inline-flex items-center px-3 py-2 rounded-lg text-[14px] transition-colors whitespace-nowrap",
				active
					? "bg-lightCta text-darkBlue font-Vazir-700"
					: "text-midGray font-Vazir-500 hover:bg-lightCta/80 hover:text-darkBlue",
			].join(" ")}
		>
			{label}
		</Link>
	);
}

function MainNavLinks({ isDoctor, onLinkClick, layout = "row" }) {
	const wrapperClass =
		layout === "col"
			? "flex flex-col gap-1 px-4"
			: "flex flex-row flex-wrap items-center justify-center gap-0.5";

	return (
		<div className={wrapperClass}>
			{NAV_ITEMS.map((item) => (
				<NavItem
					key={item.to}
					to={item.to}
					label={item.label}
					match={item.match}
					onLinkClick={onLinkClick}
				/>
			))}
			{isDoctor && (
				<NavItem
					to="/visit_doctor/"
					label="نوبت درمانگر"
					match="/visit_doctor"
					onLinkClick={onLinkClick}
				/>
			)}
		</div>
	);
}

function LoginButton({ className = "", onClick }) {
	return (
		<Link
			to="/login/"
			onClick={onClick}
			className={[
				"inline-flex items-center justify-center gap-2",
				"bg-darkBlue text-white font-Vazir-600 text-[14px]",
				"px-5 py-2.5 rounded-xl",
				"shadow-[0_2px_8px_rgba(39,106,166,0.25)]",
				"hover:bg-[#1e5a8a] active:scale-[0.98] transition-all",
				className,
			].join(" ")}
		>
			<User width={18} height={18} className="[&_path]:stroke-white" />
			ورود
		</Link>
	);
}

function ChevronDown({ open }) {
	return (
		<svg
			width="12"
			height="12"
			viewBox="0 0 12 12"
			className={`text-midGray transition-transform ${open ? "rotate-180" : ""}`}
			aria-hidden
		>
			<path
				d="M2.5 4.5L6 8L9.5 4.5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
				fill="none"
			/>
		</svg>
	);
}

function AccountDropdown({ profileId, isAdmin, onLogout }) {
	const [open, setOpen] = useState(false);
	const rootRef = useRef(null);

	useEffect(() => {
		if (!open) return;
		const onDocClick = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", onDocClick);
		return () => document.removeEventListener("mousedown", onDocClick);
	}, [open]);

	const profileTo = profileId ? `/profile/${profileId}/` : "/login/";

	return (
		<div className="relative" ref={rootRef}>
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className={[
					"inline-flex items-center gap-2.5 ps-2 pe-3 py-1.5 rounded-xl",
					"border border-[#E2E8F0] bg-white",
					"hover:border-darkBlue/25 hover:shadow-[0_2px_12px_rgba(39,106,166,0.12)]",
					"transition-all cursor-pointer",
					open ? "border-darkBlue/30 shadow-[0_2px_12px_rgba(39,106,166,0.12)]" : "",
				].join(" ")}
				aria-expanded={open}
				aria-haspopup="menu"
			>
				<span className="flex h-9 w-9 items-center justify-center rounded-full bg-lightCta">
					<User width={20} height={20} />
				</span>
				<span className="font-Vazir-600 text-[14px] text-midGray">
					حساب من
				</span>
				<ChevronDown open={open} />
			</button>
			{open && (
				<div
					className="absolute left-0 top-[calc(100%+8px)] min-w-[200px] rounded-xl border border-[#E2E8F0] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.12)] py-1.5 z-50 text-right overflow-hidden"
					role="menu"
				>
					<Link
						to={profileTo}
						className="flex items-center gap-2 px-4 py-3 font-Vazir-500 text-[14px] text-midGray hover:bg-lightCta hover:text-darkBlue transition-colors"
						role="menuitem"
						onClick={() => setOpen(false)}
					>
						پروفایل من
					</Link>
					<Link
						to="/payment_list/"
						className="flex items-center gap-2 px-4 py-3 font-Vazir-500 text-[14px] text-midGray hover:bg-lightCta hover:text-darkBlue transition-colors"
						role="menuitem"
						onClick={() => setOpen(false)}
					>
						پرداخت‌ها
					</Link>
					{isAdmin && (
						<Link
							to="/dashboard/"
							className="flex items-center gap-2 px-4 py-3 font-Vazir-500 text-[14px] text-midGray hover:bg-lightCta hover:text-darkBlue transition-colors"
							role="menuitem"
							onClick={() => setOpen(false)}
						>
							داشبورد مدیریت
						</Link>
					)}
					<div className="my-1 h-px bg-[#E2E8F0] mx-2" />
					<button
						type="button"
						className="w-full text-right px-4 py-3 font-Vazir-500 text-[14px] text-error hover:bg-red-50 border-0 bg-transparent cursor-pointer transition-colors"
						role="menuitem"
						onClick={() => {
							setOpen(false);
							onLogout();
						}}
					>
						خروج از حساب
					</button>
				</div>
			)}
		</div>
	);
}

function MobileAccountSection({ isLoggedIn, profileId, isAdmin, onLogout, onNavigate }) {
	if (!isLoggedIn) {
		return (
			<div className="px-4 pt-2">
				<LoginButton className="w-full" onClick={onNavigate} />
			</div>
		);
	}

	const profileTo = profileId ? `/profile/${profileId}/` : "/login/";

	const rowClass =
		"block w-full text-right px-4 py-3 rounded-xl font-Vazir-500 text-[15px] text-midGray hover:bg-lightCta hover:text-darkBlue transition-colors";

	return (
		<div className="mx-4 mt-4 p-3 rounded-2xl bg-lightCta/60 border border-[#E2E8F0]">
			<p className="font-Vazir-700 text-[12px] text-grayText px-2 mb-2">حساب من</p>
			<Link to={profileTo} className={rowClass} onClick={onNavigate}>
				پروفایل
			</Link>
			<Link to="/payment_list/" className={rowClass} onClick={onNavigate}>
				پرداخت‌ها
			</Link>
			{isAdmin && (
				<Link to="/dashboard/" className={rowClass} onClick={onNavigate}>
					داشبورد مدیریت
				</Link>
			)}
			<button
				type="button"
				className="w-full text-right px-4 py-3 rounded-xl font-Vazir-500 text-[15px] text-error hover:bg-white border-0 bg-transparent cursor-pointer mt-1"
				onClick={() => {
					onNavigate();
					onLogout();
				}}
			>
				خروج
			</button>
		</div>
	);
}

function HeaderBar({ children }) {
	return (
		<header className="w-full bg-white border-b border-[#E2E8F0] shadow-[0_1px_0_rgba(15,23,42,0.04)]">
			<div className="max-w-[1120px] mx-auto px-4 sm:px-6 h-[64px] flex items-center justify-between gap-4">
				{children}
			</div>
		</header>
	);
}

export default function Header({ isDesktop }) {
	const navigate = useNavigate();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const isLoggedIn = LoginHelper.isLoggedIn();
	const profileId = LoginHelper.getProfileId();
	const [profileMode, setProfileMode] = useState(LoginHelper.getProfileMode());

	useEffect(() => {
		if (!isLoggedIn) {
			return;
		}
		LoginHelper.syncProfileModeFromServer().then((mode) => {
			if (mode) {
				setProfileMode(mode);
			}
		});
	}, [isLoggedIn]);

	const isDoctor = isLoggedIn && profileMode === LoginHelper.MODE_TYPE_DOCTOR;
	const isAdmin = isLoggedIn && profileMode === LoginHelper.MODE_TYPE_ADMIN;

	const handleLogout = () => {
		LoginHelper.logout();
		navigate("/");
	};

	const closeMobileMenu = () => setIsMenuOpen(false);

	return (
		<div>
			{isDesktop ? (
				<HeaderBar>
					<Link to="/" className="shrink-0 flex items-center">
						<img src={Logo} className="h-[52px] w-[55px] object-contain" alt="همراه‌تیم" />
					</Link>
					<nav className="flex-1 flex justify-center min-w-0" aria-label="منوی اصلی">
						<MainNavLinks isDoctor={isDoctor} />
					</nav>
					<div className="shrink-0 flex items-center pl-1 border-s border-[#E2E8F0] ps-4">
						{isLoggedIn ? (
							<AccountDropdown profileId={profileId} isAdmin={isAdmin} onLogout={handleLogout} />
						) : (
							<LoginButton />
						)}
					</div>
				</HeaderBar>
			) : (
				<>
					<HeaderBar>
						<Link to="/" className="shrink-0">
							<img src={Logo} className="h-[52px] w-[55px] object-contain" alt="همراه‌تیم" />
						</Link>
						<button
							type="button"
							className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E2E8F0] bg-white hover:bg-lightCta cursor-pointer"
							onClick={() => setIsMenuOpen(!isMenuOpen)}
							aria-label="باز کردن منو"
							aria-expanded={isMenuOpen}
						>
							<Menu fill="#276AA6" height="18px" />
						</button>
					</HeaderBar>
					{isMenuOpen && (
						<div className="fixed inset-0 z-50 bg-white flex flex-col">
							<div className="h-[64px] px-4 flex items-center justify-between border-b border-[#E2E8F0]">
								<Link to="/" onClick={closeMobileMenu}>
									<img src={Logo} className="h-[52px] w-[55px] object-contain" alt="همراه‌تیم" />
								</Link>
								<button
									type="button"
									className="border-0 bg-transparent p-2 cursor-pointer"
									onClick={closeMobileMenu}
									aria-label="بستن منو"
								>
									<OrangeCross />
								</button>
							</div>
							<nav
								className="flex-1 overflow-y-auto py-4"
								aria-label="منوی موبایل"
							>
								<MainNavLinks
									isDoctor={isDoctor}
									onLinkClick={closeMobileMenu}
									layout="col"
								/>
								<MobileAccountSection
									isLoggedIn={isLoggedIn}
									profileId={profileId}
									isAdmin={isAdmin}
									onLogout={handleLogout}
									onNavigate={closeMobileMenu}
								/>
							</nav>
						</div>
					)}
				</>
			)}
			{!isDesktop && (
				<div>
					<PwaInstall />
				</div>
			)}
		</div>
	);
}
